import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBatchSchedulerStore } from './model';
import { createExecutionPlan } from './scheduler';
import { rateLimiter } from '../../services/shared/rateLimiter';
import type { ExecutionPlan, ScheduledJob } from './types';
import type { TranslationFile } from '../../types';

// These will be injected from App.tsx
type RunFileAutoFn = (fileId: string) => Promise<void>;

interface BatchExecutorDeps {
    runFileAuto: RunFileAutoFn;
    onJobComplete?: (job: ScheduledJob) => void;
    onJobError?: (job: ScheduledJob, error: Error) => void;
    onPlanComplete?: (plan: ExecutionPlan) => void;
}

/**
 * Hook to create an execution plan
 */
export function useCreatePlan() {
    const { setPlan, setPlanning } = useBatchSchedulerStore();

    return useMutation({
        mutationFn: async (files: TranslationFile[]) => {
            setPlanning(true);
            // Simulate async planning (could involve AI for smart ordering)
            await new Promise(resolve => setTimeout(resolve, 500));
            const plan = createExecutionPlan(files);
            return plan;
        },
        onSuccess: (plan) => {
            setPlan(plan);
            setPlanning(false);
        },
        onError: () => {
            setPlanning(false);
        }
    });
}

/**
 * Hook to execute a batch plan
 */
/**
 * Hook to execute a job plan (Continuous Queue Mode)
 */
export function useExecutePlan(deps: BatchExecutorDeps) {
    const {
        updateProgress,
        updatePlanStatus,
        updateJobStatus
    } = useBatchSchedulerStore();

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (plan: ExecutionPlan) => {
            const startTime = Date.now();
            updatePlanStatus('running');

            // Queue processing loop
            const processQueue = async () => {
                const store = useBatchSchedulerStore.getState();
                let isRunning = true;

                while (isRunning) {
                    // 1. Check control signals (Pause/Cancel)
                    const currentState = useBatchSchedulerStore.getState();
                    if (currentState.currentPlan?.status === 'cancelled') return;

                    if (currentState.isPaused) {
                        updatePlanStatus('paused');
                        await new Promise<void>(resolve => {
                            const unsub = useBatchSchedulerStore.subscribe(state => {
                                if (!state.isPaused || state.currentPlan?.status === 'cancelled') {
                                    unsub();
                                    resolve();
                                }
                            });
                        });
                        updatePlanStatus('running');
                        continue; // Re-check condition
                    }

                    // 2. Identify Pending and Running jobs
                    const jobs = currentState.currentPlan?.jobs || [];
                    const pendingJobs = jobs.filter(j => j.status === 'pending');
                    const runningJobs = jobs.filter(j => j.status === 'running');
                    const completedJobs = jobs.filter(j => j.status === 'completed');
                    const failedJobs = jobs.filter(j => j.status === 'failed');

                    // If all done, break
                    if (pendingJobs.length === 0 && runningJobs.length === 0) {
                        isRunning = false;
                        break;
                    }

                    // 3. Check Concurrency Limit
                    const config = currentState.config;
                    if (runningJobs.length >= config.maxConcurrent) {
                        await new Promise(r => setTimeout(r, 100)); // Wait a bit
                        continue;
                    }

                    // Helper: Check if job is blocked by previous steps for same file
                    const isJobBlocked = (job: ScheduledJob) => {
                        const SEQUENCE = ['translation', 'semantic-match', 'audit'];
                        const myIndex = SEQUENCE.indexOf(job.type);
                        if (myIndex <= 0) return false;

                        // Find incomplete lower-precedence jobs for this file
                        // blocked if there is a pending or running job with lower index for same file
                        return pendingJobs.some(j =>
                            j.fileId === job.fileId &&
                            SEQUENCE.indexOf(j.type) < myIndex &&
                            j.id !== job.id
                        ) || runningJobs.some(j =>
                            j.fileId === job.fileId &&
                            SEQUENCE.indexOf(j.type) < myIndex
                        );
                    };

                    // 4. Try to pick next UNBLOCKED job
                    // Filter pending jobs that are NOT blocked
                    const availableJobs = pendingJobs.filter(job => !isJobBlocked(job));
                    const nextJob = availableJobs[0]; // FIFO from available

                    if (nextJob) {
                        // Check Rate Limit (Sliding Window)
                        // We assume 1 request per job start (simplified)
                        if (rateLimiter.canExecute(nextJob.estimatedTokens, 1)) {

                            // EXECUTE JOB immediately
                            updateJobStatus(nextJob.id, { status: 'running', startTime: Date.now() });

                            // Fire and forget (don't await here, let the promise loop handle it)
                            // We wrap it to handle completion independently
                            (async () => {
                                try {
                                    if (nextJob.type === 'translation') {
                                        await deps.runFileAuto(nextJob.fileId);
                                    }

                                    // Record valid usage
                                    // agentRunner tự record actual tokens - không cần record ở đây

                                    updateJobStatus(nextJob.id, {
                                        status: 'completed',
                                        progress: 100,
                                        endTime: Date.now()
                                    });
                                    deps.onJobComplete?.(nextJob);

                                } catch (error: any) {
                                    updateJobStatus(nextJob.id, {
                                        status: 'failed',
                                        error: error.message
                                    });
                                    deps.onJobError?.(nextJob, error);
                                }
                            })();

                            // Move to next iteration immediately to see if we can fit another one
                            continue;
                        } else {
                            // Rate limited - wait a bit based on limiter hint
                            const waitTime = rateLimiter.getWaitTime();
                            await new Promise(r => setTimeout(r, Math.max(100, waitTime))); // Min 100ms poll
                        }
                    } else {
                        // No pending jobs, just waiting for running ones
                        await new Promise(r => setTimeout(r, 200));
                    }

                    // Update Progress Stats periodically
                    const now = Date.now();
                    updateProgress({
                        planId: plan.id,
                        completedJobs: completedJobs.length,
                        totalJobs: plan.totalJobs,
                        runningJobs: runningJobs.length,
                        failedJobs: failedJobs.length,
                        elapsedTime: now - startTime,
                        estimatedRemainingTime: 0, // TODO: Calc based on throughput
                        currentThroughputTpm: 0 // TODO: Get from rate limiter
                    });
                }
            };

            await processQueue();

            updatePlanStatus('completed');
            deps.onPlanComplete?.(plan);
            queryClient.invalidateQueries({ queryKey: ['files'] });
        },
        onError: (error) => {
            console.error('Queue execution failed:', error);
            updatePlanStatus('cancelled');
        }
    });
}

/**
 * Hook to pause/resume execution
 */
export function usePauseResume() {
    const { isPaused, pause, resume } = useBatchSchedulerStore();

    return {
        isPaused,
        pause,
        resume,
        toggle: () => isPaused ? resume() : pause()
    };
}

/**
 * Hook to cancel execution
 */
export function useCancelExecution() {
    const { cancel, reset } = useBatchSchedulerStore();

    return {
        cancel,
        reset
    };
}
